/**
 * CANDADO BIOMÉTRICO DE LA PWA — gemelo de `BiometricGate.kt` de Android.
 *
 * Es una PUERTA LOCAL, no un login: la sesión ya está guardada en el
 * dispositivo y lo que se pide es que quien abre la app sea quien tiene el
 * teléfono desbloqueado (Face ID / Touch ID / huella / PIN del sistema).
 * Se usa WebAuthn con el autenticador de la plataforma y sin servidor: se crea
 * una credencial una vez y al abrir se pide una firma sobre un desafío local.
 * No se verifica la firma (no hay contra qué) — lo que vale es que el sistema
 * exigió la verificación de usuario para producirla.
 *
 * Límite honesto, igual que en Android: protege contra quien agarra el
 * teléfono desbloqueado y abre FORJA, no contra quien lee el localStorage.
 *
 * Crear la credencial EXIGE un gesto del usuario (iOS/Safari), por eso el alta
 * no puede ser silenciosa: la ofrece una hoja con un botón.
 */

import { isIos, isStandalone } from "./install";
import { esNativo } from "./nativo";
import {
  apagarCandadoNativo,
  candadoNativoEncendido,
  encenderCandadoNativo,
  hayBiometriaNativa,
  pedirVerificacion,
} from "./biometriaNativa";

const KEY_CRED = "forja.lock.cred";
const KEY_OFF = "forja.lock.off";
const KEY_ABIERTO = "forja.lock.abierto";

// Memoria de ESTE arranque: un arranque en frío vuelve a pedir; volver de
// segundo plano con la app viva no (igual que Android). Vive en sessionStorage
// y no en una variable: la actualización automática de la PWA RECARGA la
// página al cambiar de pantalla o al volver de segundo plano, y con la variable
// en memoria esa recarga volvía a pedir la huella en pleno uso. sessionStorage
// sobrevive a la recarga y muere cuando el sistema cierra la app — exactamente
// el "arranque en frío". La variable queda como respaldo si no hay storage.
let desbloqueadoEnEsteArranque = false;

function marcarDesbloqueado(): void {
  desbloqueadoEnEsteArranque = true;
  try {
    sessionStorage.setItem(KEY_ABIERTO, "1");
  } catch {
    // Sin sessionStorage queda la variable: peor caso, pide de nuevo al recargar.
  }
}

function aBase64Url(buf: ArrayBuffer): string {
  let s = "";
  new Uint8Array(buf).forEach((b) => {
    s += String.fromCharCode(b);
  });
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function deBase64Url(s: string): Uint8Array<ArrayBuffer> {
  const b64 = s.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i += 1) out[i] = bin.charCodeAt(i);
  return out;
}

function desafio(): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(new ArrayBuffer(32));
  crypto.getRandomValues(out);
  return out;
}

function leer(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function escribir(key: string, valor: string | null): void {
  try {
    if (valor === null) localStorage.removeItem(key);
    else localStorage.setItem(key, valor);
  } catch {
    // Sin storage no hay candado: mejor sin puerta que una puerta que no abre.
  }
}

/** El dispositivo tiene con qué verificar al usuario (biometría o PIN). */
export async function biometriaDisponible(): Promise<boolean> {
  try {
    // ADENTRO DE LA APP pregunta el plugin nativo, no WebAuthn: en el WebView
    // no hay autenticador de plataforma y `rp.id` sería `localhost` (ver
    // `activarCandado`). Va ANTES del chequeo de `PublicKeyCredential`, porque
    // en el contenedor ese objeto puede existir y no servir para nada.
    if (esNativo()) return hayBiometriaNativa();
    if (typeof window === "undefined" || !window.PublicKeyCredential) return false;
    return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Hay credencial creada en este dispositivo = el candado está encendido. */
export function candadoActivo(): boolean {
  if (esNativo()) return candadoNativoEncendido();
  return leer(KEY_CRED) !== null;
}

/** El usuario dijo "ahora no" / lo apagó: no se le vuelve a ofrecer solo. */
export function candadoRechazado(): boolean {
  return leer(KEY_OFF) === "1";
}

export function yaDesbloqueado(): boolean {
  if (desbloqueadoEnEsteArranque) return true;
  try {
    return sessionStorage.getItem(KEY_ABIERTO) === "1";
  } catch {
    return false;
  }
}

/** La puerta se abrió por otro camino (contraseña o código al mail). */
export function abrirPuerta(): void {
  marcarDesbloqueado();
}

/**
 * Cómo lo llama el teléfono: en iPhone es Face ID; en Android, la huella. Va
 * con el posesivo para que lea bien en todas las frases ("Ingresar con tu
 * huella", "¿Entrar con Face ID?").
 */
export function nombreBiometria(): string {
  return isIos() ? "Face ID" : "tu huella";
}

export function rechazarCandado(): void {
  escribir(KEY_OFF, "1");
}

export function apagarCandado(): void {
  if (esNativo()) apagarCandadoNativo();
  escribir(KEY_CRED, null);
  escribir(KEY_OFF, "1");
}

/**
 * Tira la llave de este teléfono SIN apagar el candado por decisión: quien
 * tuvo que entrar con contraseña desde la puerta vuelve a ver la oferta y la
 * crea de nuevo. Es la salida del caso "borré la llave del llavero": el
 * navegador contesta igual que si hubiera cancelado (NotAllowedError, para no
 * revelar qué llaves existen), así que la puerta no puede distinguirlo sola.
 */
export function olvidarLlave(): void {
  escribir(KEY_CRED, null);
}

/**
 * Hay que preguntarle por el candado: app instalada, el teléfono puede y nunca
 * dijo ni que sí ni que no. Lo usa el pedido de avisos para no encimar hojas.
 */
export async function ofertaPendiente(): Promise<boolean> {
  if (!isStandalone() || candadoActivo() || candadoRechazado()) return false;
  return biometriaDisponible();
}

/**
 * Crea la credencial y enciende el candado. Tiene que llamarse desde el click
 * de un botón. Devuelve true si quedó encendido.
 */
export async function activarCandado(user: { id: string; email: string; nombre: string }): Promise<boolean> {
  // En nativo no hay credencial que crear: Face ID contesta sí o no. El `user`
  // no se usa, y es correcto que no se use (ver `biometriaNativa.ts`).
  if (esNativo()) {
    const ok = await encenderCandadoNativo();
    if (ok) {
      escribir(KEY_OFF, null);
      marcarDesbloqueado();
    }
    return ok;
  }
  try {
    const cred = (await navigator.credentials.create({
      publicKey: {
        challenge: desafio(),
        rp: { name: "FORJA", id: window.location.hostname },
        user: {
          id: new TextEncoder().encode(user.id),
          name: user.email,
          displayName: user.nombre || user.email,
        },
        pubKeyCredParams: [
          { type: "public-key", alg: -7 },
          { type: "public-key", alg: -257 },
        ],
        authenticatorSelection: {
          authenticatorAttachment: "platform",
          userVerification: "required",
          residentKey: "discouraged",
        },
        timeout: 60_000,
        attestation: "none",
      },
    })) as PublicKeyCredential | null;
    if (!cred) return false;
    escribir(KEY_CRED, aBase64Url(cred.rawId));
    escribir(KEY_OFF, null);
    // Quien acaba de verificarse para activarlo no tiene que hacerlo de nuevo.
    marcarDesbloqueado();
    return true;
  } catch {
    return false;
  }
}

export type ResultadoDesbloqueo = "ok" | "cancelado" | "no_disponible";

/** Pide la verificación del sistema. Se llama desde el gate. */
export async function desbloquear(): Promise<ResultadoDesbloqueo> {
  if (esNativo()) {
    if (!candadoNativoEncendido()) return "no_disponible";
    const r = await pedirVerificacion("Para entrar a FORJA");
    if (r === "ok") marcarDesbloqueado();
    return r;
  }
  const id = leer(KEY_CRED);
  if (!id) return "no_disponible";
  try {
    const res = await navigator.credentials.get({
      publicKey: {
        challenge: desafio(),
        rpId: window.location.hostname,
        allowCredentials: [{ type: "public-key", id: deBase64Url(id), transports: ["internal"] }],
        userVerification: "required",
        timeout: 60_000,
      },
    });
    if (!res) return "cancelado";
    marcarDesbloqueado();
    return "ok";
  } catch (err) {
    // NotAllowedError = canceló, expiró, el navegador no dejó dispararlo sin un
    // toque, O la llave ya no existe (los navegadores no distinguen, a
    // propósito). Por eso la puerta nunca depende sólo de esto: siempre tiene
    // "Usar contraseña". El resto = el teléfono no puede verificar.
    const nombre = err instanceof DOMException ? err.name : "";
    if (nombre === "NotAllowedError" || nombre === "AbortError") return "cancelado";
    return "no_disponible";
  }
}
